import {
  Button,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  StackDirection,
  useBreakpointValue,
  useToast,
} from "@chakra-ui/react";
import { MobileLinks } from "components";
import { getSession, setSession } from "features/auth/slice";
import { ResponseInterface, SessionCreateInterface } from "features/auth/types";
import { useFormik } from "formik";
import { useTranslation } from "languages";
import { useAppDispatch, useAppSelector } from "tools/store";
import { ShowToast } from "utils/Helpers";
import * as Yup from "yup";
import { useEditUserMutation } from "../service";

const ProfileEdit: React.FC = () => {
  const dispatch = useAppDispatch();
  const userData = useAppSelector(getSession);
  const toast = useToast();
  const { translation } = useTranslation();

  const [onUpdateUser, { isLoading }] = useEditUserMutation();

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email("invalidEmailAddress")
      .required("emailIsRequired"),
    full_name: Yup.string().required("fullIsRequired"),
    last_name: Yup.string().required("lastIsRequired"),

    // contact: Yup.number().required('contactIsRequired'),
    // password: Yup.string().required('passwordIsRequired'),
  });

  const { handleBlur, handleChange, values, handleSubmit, errors, touched } =
    useFormik({
      initialValues: {
        email: userData?.user?.email,
        full_name: userData?.user?.fname,
        last_name: userData?.user?.lname,
      },
      validationSchema,
      onSubmit: async (values: any) => {
        const response: ResponseInterface<SessionCreateInterface> =
          await onUpdateUser({ ...values, id: userData?.user?.id });
        // console.log("=-=-=-=-", response)
        if (!response.error && response.data) {
          dispatch(setSession(response.data));
          ShowToast(toast, translation["profile_updated"], "success");
        } else {
          ShowToast(toast, translation["someThing"], "error");
        }
        // } else ToastStore.showToast('someThing', 'error');
      },
    });

  const layoutDirection = useBreakpointValue({ base: "column", md: "row" });

  return (
    <Stack
      direction={layoutDirection as StackDirection}
      // w={"80%"}
      w={"full"}
      h="100%"
      justifySelf={"flex-end"}
      bgGradient="linear(to-r, transparent, secondary.950 40%)"
      pr={[5, "180px"]}
      pl={[5, 0]}
      py={[5, 0]}
      overflow={"auto"}
    >
      {layoutDirection === "row" && <MobileLinks />}

      <Stack
        w={["100%", "35%"]}
        // h={"100%"}
        align={"center"}
        justify={"center"}
        pos={"relative"}
      >
        <Heading size="xl" alignSelf={"flex-start"}>
          {translation["edit_profile"]}
        </Heading>

        <HStack justify={"flex-start"} w={"100%"} my={10}>
          <svg
            id="profile-circle"
            xmlns="http://www.w3.org/2000/svg"
            width="80"
            height="80"
            viewBox="0 0 80 80"
          >
            <g
              id="vuesax_outline_profile-circle"
              data-name="vuesax/outline/profile-circle"
            >
              <g id="profile-circle-2" data-name="profile-circle">
                <path
                  id="Vector"
                  d="M13.9,26.832h-.666a13.718,13.718,0,0,1-9.454-4.061A13.256,13.256,0,0,1,0,13.434a13.434,13.434,0,1,1,13.9,13.4Zm-.536-5.023c.142,0,.309.009.47.025A8.438,8.438,0,1,0,4.968,13.4a8.4,8.4,0,0,0,8.1,8.433A1.166,1.166,0,0,1,13.363,21.809Z"
                  transform="translate(26.602 18.267)"
                  fill="#bf8d46"
                />
                <path
                  id="Vector-2"
                  data-name="Vector"
                  d="M24.979,26.85a35.737,35.737,0,0,1-24.168-9.4,2.52,2.52,0,0,1-.8-2.1c.437-3.989,2.924-7.683,7-10.4A32.774,32.774,0,0,1,24.99,0,32.65,32.65,0,0,1,42.943,4.95c4.082,2.753,6.569,6.447,7,10.4a2.386,2.386,0,0,1-.8,2.1A35.733,35.733,0,0,1,24.979,26.85Zm.007-21.866a27.844,27.844,0,0,0-15.208,4.1,10.694,10.694,0,0,0-4.533,5.6,30.776,30.776,0,0,0,19.734,7.167A30.77,30.77,0,0,0,44.71,14.682a10.9,10.9,0,0,0-4.566-5.6A27.6,27.6,0,0,0,24.986,4.984Z"
                  transform="translate(15.025 48.984)"
                  fill="#bf8d46"
                />
                <path
                  id="Vector-3"
                  data-name="Vector"
                  d="M80,0V80H0V0Z"
                  fill="#bf8d46"
                  opacity="0"
                />
                <path
                  id="Vector-4"
                  data-name="Vector"
                  d="M35.832,71.667A35.833,35.833,0,1,1,71.667,35.832,35.874,35.874,0,0,1,35.832,71.667ZM35.832,5A30.833,30.833,0,1,0,66.666,35.832,30.866,30.866,0,0,0,35.832,5Z"
                  transform="translate(4.167 4.167)"
                  fill="#bf8d46"
                />
              </g>
            </g>
          </svg>

          <Heading size="xl" ml={5}>
            {userData?.user?.fname} {userData?.user?.lname}
          </Heading>
        </HStack>

        <InputGroup w={"100%"}>
          <InputLeftElement pointerEvents="none" h={"100%"}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20.742"
              height="20.742"
              viewBox="0 0 20.742 20.742"
            >
              <g
                id="vuesax_outline_frame"
                data-name="vuesax/outline/frame"
                transform="translate(-108 -252)"
              >
                <g id="frame" transform="translate(108 252)">
                  <path
                    id="Vector"
                    d="M4.624,8.962H4.555a.469.469,0,0,0-.156,0,4.447,4.447,0,1,1,.251,0ZM4.485,1.3a3.186,3.186,0,0,0-.121,6.37,1.427,1.427,0,0,1,.277,0A3.187,3.187,0,0,0,4.485,1.3Z"
                    transform="translate(5.885 1.08)"
                    fill="#ecdfc9"
                  />
                  <path
                    id="Vector-2"
                    data-name="Vector"
                    d="M0,0H20.742V20.742H0Z"
                    fill="none"
                    opacity="0"
                  />
                  <path
                    id="Vector-3"
                    data-name="Vector"
                    d="M6.542,8.74a8.537,8.537,0,0,1-4.684-1.3A3.709,3.709,0,0,1,0,4.375,3.747,3.747,0,0,1,1.858,1.29a9.15,9.15,0,0,1,9.369,0,3.729,3.729,0,0,1,1.858,3.068,3.747,3.747,0,0,1-1.858,3.085A8.558,8.558,0,0,1,6.542,8.74ZM2.575,2.379A2.485,2.485,0,0,0,1.3,4.384,2.48,2.48,0,0,0,2.575,6.372a7.81,7.81,0,0,0,7.934,0,2.485,2.485,0,0,0,1.279-2.005,2.48,2.48,0,0,0-1.279-1.988A7.851,7.851,0,0,0,2.575,2.379Z"
                    transform="translate(3.975 10.749)"
                    fill="#ecdfc9"
                  />
                </g>
              </g>
            </svg>
          </InputLeftElement>

          <Input
            type="text"
            name="full_name"
            placeholder={translation["first_name"]}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.full_name}
            borderRadius={10}
            isInvalid={touched.full_name && Boolean(errors.full_name)}
            errorBorderColor="crimson"
          />
        </InputGroup>
        <InputGroup w={"100%"}>
          <InputLeftElement pointerEvents="none" h={"100%"}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20.742"
              height="20.742"
              viewBox="0 0 20.742 20.742"
            >
              <g
                id="vuesax_outline_frame"
                data-name="vuesax/outline/frame"
                transform="translate(-108 -252)"
              >
                <g id="frame" transform="translate(108 252)">
                  <path
                    id="Vector"
                    d="M4.624,8.962H4.555a.469.469,0,0,0-.156,0,4.447,4.447,0,1,1,.251,0ZM4.485,1.3a3.186,3.186,0,0,0-.121,6.37,1.427,1.427,0,0,1,.277,0A3.187,3.187,0,0,0,4.485,1.3Z"
                    transform="translate(5.885 1.08)"
                    fill="#ecdfc9"
                  />
                  <path
                    id="Vector-2"
                    data-name="Vector"
                    d="M0,0H20.742V20.742H0Z"
                    fill="none"
                    opacity="0"
                  />
                  <path
                    id="Vector-3"
                    data-name="Vector"
                    d="M6.542,8.74a8.537,8.537,0,0,1-4.684-1.3A3.709,3.709,0,0,1,0,4.375,3.747,3.747,0,0,1,1.858,1.29a9.15,9.15,0,0,1,9.369,0,3.729,3.729,0,0,1,1.858,3.068,3.747,3.747,0,0,1-1.858,3.085A8.558,8.558,0,0,1,6.542,8.74ZM2.575,2.379A2.485,2.485,0,0,0,1.3,4.384,2.48,2.48,0,0,0,2.575,6.372a7.81,7.81,0,0,0,7.934,0,2.485,2.485,0,0,0,1.279-2.005,2.48,2.48,0,0,0-1.279-1.988A7.851,7.851,0,0,0,2.575,2.379Z"
                    transform="translate(3.975 10.749)"
                    fill="#ecdfc9"
                  />
                </g>
              </g>
            </svg>
          </InputLeftElement>

          <Input
            type="text"
            name="last_name"
            placeholder={translation["last_name"]}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.last_name}
            borderRadius={10}
            isInvalid={touched.last_name && Boolean(errors.last_name)}
            errorBorderColor="crimson"
          />
        </InputGroup>

        {/* <InputGroup>
          <InputLeftElement pointerEvents='none' h={'100%'}>
            <svg id="mobile" xmlns="http://www.w3.org/2000/svg" width="20.742" height="20.742" viewBox="0 0 20.742 20.742">
              <path id="Vector" d="M10.155,18.581H4.969C1.158,18.581,0,17.423,0,13.612V4.969C0,1.158,1.158,0,4.969,0h5.185c3.811,0,4.969,1.158,4.969,4.969v8.642C15.124,17.423,13.966,18.581,10.155,18.581ZM4.969,1.3C1.875,1.3,1.3,1.884,1.3,4.969v8.642c0,3.085.579,3.673,3.673,3.673h5.185c3.094,0,3.673-.588,3.673-3.673V4.969c0-3.085-.579-3.673-3.673-3.673Z" transform="translate(2.809 1.08)" fill="#ecdfc9" />
              <path id="Vector-2" data-name="Vector" d="M4.105,1.3H.648A.653.653,0,0,1,0,.648.653.653,0,0,1,.648,0H4.105a.653.653,0,0,1,.648.648A.653.653,0,0,1,4.105,1.3Z" transform="translate(7.994 4.105)" fill="#ecdfc9" />
              <path id="Vector-3" data-name="Vector" d="M1.988,3.975A1.988,1.988,0,1,1,3.975,1.988,1.988,1.988,0,0,1,1.988,3.975Zm0-2.688a.691.691,0,1,0,.691.691A.693.693,0,0,0,1.988,1.288Z" transform="translate(8.383 13.188)" fill="#ecdfc9" />
              <path id="Vector-4" data-name="Vector" d="M0,0H20.742V20.742H0Z" fill="none" opacity="0" />
            </svg>
          </InputLeftElement>

          <Input
            type="text"
            name="contact"
            placeholder="Contact"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.contact}
            borderRadius={10}
            // size='lg'
            isInvalid={touched.contact && Boolean(errors.contact)}
            errorBorderColor='crimson'
          />
        </InputGroup> */}

        <InputGroup>
          <InputLeftElement pointerEvents="none" h={"100%"}>
            <svg
              id="vuesax_outline_sms"
              data-name="vuesax/outline/sms"
              xmlns="http://www.w3.org/2000/svg"
              width="20.096"
              height="20.096"
              viewBox="0 0 20.096 20.096"
            >
              <g id="sms">
                <path
                  id="Vector"
                  d="M13.188,15.491H4.815C1.758,15.491,0,13.732,0,10.676V4.815C0,1.758,1.758,0,4.815,0h8.373C16.244,0,18,1.758,18,4.815v5.861C18,13.732,16.244,15.491,13.188,15.491ZM4.815,1.256c-2.395,0-3.559,1.164-3.559,3.559v5.861c0,2.395,1.164,3.559,3.559,3.559h8.373c2.395,0,3.559-1.164,3.559-3.559V4.815c0-2.395-1.164-3.559-3.559-3.559Z"
                  transform="translate(1.047 2.303)"
                  fill="#ecdfc9"
                />
                <path
                  id="Vector-2"
                  data-name="Vector"
                  d="M4.817,3.873a3.113,3.113,0,0,1-1.959-.661L.237,1.119a.626.626,0,0,1,.779-.98L3.636,2.232a2,2,0,0,0,2.353,0L8.61.139a.618.618,0,0,1,.879.1.618.618,0,0,1-.1.879L6.768,3.212A3.073,3.073,0,0,1,4.817,3.873Z"
                  transform="translate(5.231 6.903)"
                  fill="#ecdfc9"
                />
                <path
                  id="Vector-3"
                  data-name="Vector"
                  d="M0,0H20.1V20.1H0Z"
                  fill="none"
                  opacity="0"
                />
              </g>
            </svg>
          </InputLeftElement>

          <Input
            type="text"
            name="email"
            placeholder="Your Email"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.email}
            borderRadius={10}
            disabled
            isInvalid={touched.email && Boolean(errors.email)}
            errorBorderColor="crimson"
          />
        </InputGroup>

        <Button
          w={"100%"}
          isLoading={isLoading}
          onClick={() => {
            handleSubmit();
          }}
        >
          Update
        </Button>
      </Stack>
    </Stack>
  );
};

export default ProfileEdit;
